import DismissIcon from './DismissIcon';
import PencilIcon from './PencilIcon';

function FavoriteGame({
  game,
  index,
  totalGames,
  isEditMode,
  onMove,
  onGameLinkClick,
  onDeleteCustomLink,
  onRemoveFavorite,
  dimGame,
  dimScoreButton,
  onAddScore,
  isRevealed,
  onReveal
}) {

  const isCustomLink = game.isCustom;
  const isPageBreak = game.isPageBreak;

  // Outside edit mode a page break is either a "View More" button (not yet
  // revealed) or nothing at all (revealed - the items after it render on
  // their own, and the break leaves no trace once it's done its job).
  if (isPageBreak && !isEditMode) {
    if (isRevealed) return null;
    return (
      <li className="border-b border-white/5 last:border-none">
        <div className="flex justify-center py-2">
          <button
            onClick={onReveal}
            className="text-sm font-bold text-nlg-gold px-4 py-1 rounded-full border border-nlg-gold/40 transition-colors duration-150 hover:bg-white/5"
          >
            View More
          </button>
        </div>
      </li>
    );
  }

  return (
    <li
      key={game.id}
      className={`flex justify-between items-center gap-2 py-2 px-3 border-b border-white/5 last:border-none transition-colors duration-150 hover:bg-white/5 ${dimGame ? 'bg-white/5' : ''}`}
    >

      {isPageBreak ? (
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="flex-1 border-t border-dashed border-nlg-rule" />
          <span className="font-display font-bold text-sm text-nlg-muted whitespace-nowrap">Page break</span>
          <div className="flex-1 border-t border-dashed border-nlg-rule" />
        </div>
      ) : (
        <a
          href={game.url}
          className="flex items-center gap-3 flex-1 min-w-0"
          onClick={(e) => {
            // Only track non-custom links (actual games)
            if (!game.isCustom) {
              e.preventDefault();
              onGameLinkClick(game);
            }
          }}
        >
          {isCustomLink ? (
            <div
              className="w-[50px] h-[50px] rounded-lg flex-shrink-0 flex items-center justify-center text-4xl"
              style={{ backgroundColor: game.backgroundColor || '#4F46E5' }}
            >
              {game.emoji || '🎮'}
            </div>
          ) : (
            <img
              src={"/img/games/"+game.image}
              className="w-[50px] h-[50px] rounded-lg border-2 border-black flex-shrink-0"
              alt={game.name}
            />
          )}
          <p
            className={`font-display font-bold text-left text-[22px] sm:text-[26px] ${dimGame ? 'opacity-50' : ''}`}
            style={{ color: '#fef9ef' }}
          >
            {game.name}
          </p>
        </a>
      )}

      {/* Score entry button - only show for actual games (not custom links), not
          in edit mode, and when the game has a scoreType. No account needed:
          logging the first score is what creates one (D22).

          Two dim states, and they mean different things: the row dims once the
          game has been opened today, and this button dims only once a score is
          actually logged. A dimmed row beside a full-brightness button is the
          "you played - now log it" state, not a bug. Still clickable when
          dimmed, since editing today's score is the point. */}
      {!isEditMode && !game.isCustom && game.scoreType && onAddScore && (
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onAddScore(game.id);
          }}
          className={`w-8 h-8 flex items-center justify-center rounded-full transition-transform duration-150 hover:scale-105 flex-shrink-0 ${
            dimScoreButton
              ? 'bg-transparent border border-nlg-gold/40 text-nlg-gold/50'
              : 'bg-nlg-gold text-nlg-bg'
          }`}
          title={dimScoreButton ? "Edit today's score" : 'Enter Score'}
        >
          <PencilIcon />
        </button>
      )}

      {/* Control buttons - only show in edit mode */}
      {isEditMode && (
        <div className="flex items-center gap-2.5 flex-shrink-0">
          {/* Up button */}
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onMove(index, -1);
            }}
            disabled={index === 0}
            className={`w-7 h-7 flex items-center justify-center rounded-full transition-colors duration-150 ${
              index === 0
                ? 'cursor-not-allowed bg-white/5 text-black'
                : 'bg-nlg-gold text-nlg-bg'
            }`}
            title="Move up"
          >
            <svg className="w-5 h-7" fill="currentColor" viewBox="0 0 24 24">
              <path d="M4 18l8-14 8 14z"/>
            </svg>
          </button>

          {/* Down button */}
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onMove(index, 1);
            }}
            disabled={index === totalGames - 1}
            className={`w-7 h-7 flex items-center justify-center rounded-full transition-colors duration-150 ${
              index === totalGames - 1
                ? 'cursor-not-allowed bg-white/5 text-black'
                : 'bg-nlg-gold text-nlg-bg'
            }`}
            title="Move down"
          >
            <svg className="w-5 h-7" fill="currentColor" viewBox="0 0 24 24">
              <path d="M4 6l8 14 8-14z"/>
            </svg>
          </button>

          {/* Delete button */}
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (isCustomLink) {
                onDeleteCustomLink(game.id);
              } else {
                onRemoveFavorite(game.id);
              }
            }}
            className="w-7 h-7 flex items-center justify-center bg-nlg-gold text-black rounded-full transition-transform duration-150 hover:scale-105"
            title={isCustomLink ? "Delete custom link" : isPageBreak ? "Remove page break" : "Remove from favorites"}
          >
            <DismissIcon className="w-4 h-4" />
          </button>
        </div>
      )}
    </li>
  );
}

export default FavoriteGame;
